import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { books } from "@/data/content";

const tones = {
  bordeaux: "bg-bordeaux text-paper",
  sand: "bg-sand text-ink",
  ink: "bg-ink text-paper",
  terracotta: "bg-terracotta text-paper",
  elevated: "bg-elevated text-ink",
  ivory: "bg-paper2 text-ink",
};

const badgeStyle = (status) => {
  if (status === "Disponibile su Amazon") return "bg-bordeaux text-paper";
  if (status === "In uscita") return "border border-ink text-ink";
  return "border border-ink/30 text-ink2";
};

export function BookCover({ book }) {
  const tone = tones[book.coverTone] || tones.ivory;
  return (
    <div
      className={`relative aspect-[2/3] w-full overflow-hidden transition-transform duration-500 ease-out group-hover:scale-[1.025] ${tone}`}
    >
      {book.coverImage ? (
        <img
          src={book.coverImage}
          alt={`Copertina di ${book.title}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <>
          <div className="absolute inset-3 border border-current opacity-25 pointer-events-none" />
          <div className="absolute inset-0 flex flex-col justify-between p-6 sm:p-7">
            <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] opacity-80">
              {book.author}
            </p>
            <p className="font-serif italic text-[1.65rem] sm:text-3xl leading-[1.15] pr-4">
              {book.title}
            </p>
            <p className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.3em] opacity-70">
              {book.genre || book.language || "Luisa Miceli"}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export function BookCard({ book, onOpen }) {
  return (
    <div className="group" data-testid={`book-card-${book.slug}`}>
      <button
        onClick={() => onOpen(book)}
        className="block w-full text-left"
        aria-label={`Apri la scheda di ${book.title}`}
        data-testid={`book-cover-button-${book.slug}`}
      >
        <BookCover book={book} />
      </button>
      <div className="mt-6 flex items-baseline justify-between gap-4">
        <h3 className="font-serif text-2xl leading-snug">{book.title}</h3>
        {book.status && (
          <span
            className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1 ${badgeStyle(book.status)}`}
            data-testid={`book-status-badge-${book.slug}`}
          >
            {book.status}
          </span>
        )}
      </div>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted3">
        {book.author}
      </p>
      <p className="mt-3 text-sm leading-relaxed text-ink2">{book.description}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
        <button
          onClick={() => onOpen(book)}
          className="link-underline text-sm tracking-wide"
          data-testid={`book-detail-button-${book.slug}`}
        >
          Scopri il libro
        </button>
        {book.amazonUrl && (
          <a
            href={book.amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-ink px-5 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-paper"
            data-testid={`book-amazon-button-${book.slug}`}
          >
            Acquista su Amazon
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
        )}
      </div>
    </div>
  );
}

export function BookModal({ book, onClose }) {
  if (!book) return null;
  return (
    <Dialog open={!!book} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl bg-paper border border-ink/15 p-0 gap-0 max-h-[90vh] overflow-y-auto"
        data-testid="book-detail-modal"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="p-6 sm:p-8 flex items-center bg-paper2">
            <div className="w-full max-w-[240px] mx-auto">
              <BookCover book={book} />
            </div>
          </div>
          <div className="p-6 sm:p-10">
            <DialogHeader className="text-left space-y-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bordeaux mb-3">
                {book.author}
              </p>
              <DialogTitle className="font-serif text-3xl leading-tight text-ink">
                {book.title}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="mt-4 text-sm leading-relaxed text-ink2">
              {book.descriptionLong || book.description}
            </DialogDescription>

            <dl className="mt-6 space-y-2 text-sm">
              {book.amazonTitle && (
                <div className="flex gap-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted3 w-20 shrink-0 pt-0.5">
                    Edizione
                  </dt>
                  <dd className="text-ink">{book.amazonTitle}</dd>
                </div>
              )}
              {book.genre && (
                <div className="flex gap-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted3 w-20 shrink-0 pt-0.5">
                    Genere
                  </dt>
                  <dd className="text-ink">{book.genre}</dd>
                </div>
              )}
              {book.language && (
                <div className="flex gap-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted3 w-20 shrink-0 pt-0.5">
                    Lingua
                  </dt>
                  <dd className="text-ink">{book.language}</dd>
                </div>
              )}
              {book.themes && (
                <div className="flex gap-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted3 w-20 shrink-0 pt-0.5">
                    Temi
                  </dt>
                  <dd className="text-ink">{book.themes}</dd>
                </div>
              )}
              {book.status && (
                <div className="flex gap-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted3 w-20 shrink-0 pt-0.5">
                    Stato
                  </dt>
                  <dd className="text-ink">{book.status}</dd>
                </div>
              )}
            </dl>

            <div className="mt-8 flex flex-wrap items-center gap-5">
              {book.amazonUrl && (
                <a
                  href={book.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-ink text-paper px-6 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors hover:bg-bordeaux"
                  data-testid="book-modal-amazon-button"
                >
                  Acquista su Amazon
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
              )}
              {book.amazonPaperbackUrl && (
                <a
                  href={book.amazonPaperbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted3 link-underline"
                  data-testid="book-modal-paperback-link"
                >
                  Versione cartacea
                </a>
              )}
              <Link
                to="/contatti"
                className="link-underline text-sm"
                onClick={onClose}
                data-testid="book-modal-contact-link"
              >
                Sei un editore? Parliamone
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BookShelf({ slugs = null }) {
  const list = slugs ? books.filter((b) => slugs.includes(b.slug)) : books;
  return list;
}
